/**
 * /dashboard/expenses/$id — Expense detail (Phase 3d).
 *
 * IMPORTANT — there is NO GET /expenses/{id} endpoint and no
 * expense-timeline table (deliberate; same pattern as leave 4.3). The
 * single expense is DERIVED from the role-scoped list query
 * (useExpenseFromList) — the same list the index page uses, so the row
 * reflects whatever the caller's SQL scope allows and stays consistent
 * after a submit/decision/delete invalidation.
 *
 * The "timeline" here is NOT a synthetic event feed — it is the
 * expense's own lifecycle fields (created_at → status, decided_at,
 * decision_notes, reimbursed_batch_id) presented as an honest status &
 * decision summary.
 *
 * Migration note: port of
 * frontend/src/app/(dashboard)/dashboard/expenses/[id]/page.tsx.
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - next/link → @tanstack/react-router Link
 */

import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, CalendarDays, FileText, Store } from "lucide-react";
import {
  useExpenseCategories,
  useExpenseFromList,
  useExpenseReceipts,
} from "@/hooks/use-expenses";
import {
  showFiledRow,
  showReceiptRequiredWarning,
  undecidedLine,
} from "@/lib/expense-detail-copy";
import { useDirectory } from "@/hooks/use-directory";
import { toApiError } from "@/lib/api";
import {
  fmtDate,
  fmtDateTime,
  formatPaise,
} from "@/lib/expense-format";
import { useAuth } from "@/lib/auth";
import { useExpenseRoles } from "@/components/expenses/use-expense-roles";
import { ExpensesModuleShell } from "@/components/shared/expenses-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { ExpenseFeatureGate } from "@/components/expenses/expense-feature-gate";
import { ExpenseActions } from "@/components/expenses/expense-actions";
import { ReceiptManager } from "@/components/expenses/receipt-manager";
import { RequestSurfaceClaim } from "@/components/dashboard/surface-claim";
import { fromSearchSchema } from "@/lib/from-search";
import { decidedByLabel, decidedElsewhereLine } from "@/lib/approval-copy";
import { requestDetailBack } from "@/lib/request-detail-nav";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

import { EditDraftExpenseDialog } from "@/components/expenses/edit-draft-expense-dialog";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/expenses/$id",
)({
  // ?from=me|manage|inbox — the side that opened it (lib/from-search.ts).
  validateSearch: fromSearchSchema,
  component: ExpenseDetailRoute,
});

function Row({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border border-input px-3 py-2">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-right text-sm font-medium text-gray-900">
        {value}
      </dd>
    </div>
  );
}

function ExpenseDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const { user } = useAuth();
  const { isExpenseAdmin } = useExpenseRoles();
  const { expense, isLoading, isError, error } =
    useExpenseFromList(numId);
  const catsQ = useExpenseCategories();
  const dir = useDirectory({ includeInactive: true });
  // Same query (and cache key) the ReceiptManager below uses, so the
  // "receipt required" warning clears the moment an upload lands.
  const receiptsQ = useExpenseReceipts(numId, !!expense);

  const category = catsQ.data?.categories.find(
    (c) => c.id === expense?.category_id,
  );
  const categoryLabel = expense
    ? (category?.name ?? `Category #${expense.category_id}`)
    : "";

  const isOwner = !!expense && user?.user_id === expense.user_id;
  const canModify = isOwner || isExpenseAdmin;
  // Back follows ?from= (Inbox / Manage / Me); someone else's claim
  // opened from a notification goes back to the Inbox (persona round).
  // A decision taken from the Inbox returns there (lib/request-detail-nav).
  const { from } = Route.useSearch();
  const navigate = useNavigate();
  const back = requestDetailBack({
    kind: "expenses",
    from,
    isOwner: expense && user ? expense.user_id === user.user_id : null,
  });
  const onDecided = back.returnToInbox
    ? () => void navigate({ to: back.to, params: { tenantSlug }, search: back.search })
    : undefined;
  const decidedElsewhere = expense
    ? decidedElsewhereLine({
        status: expense.status,
        deciderId: expense.approver_id,
        deciderName: dir.label(expense.approver_id, expense.approver_name),
        decidedAtLabel: expense.decided_at ? fmtDateTime(expense.decided_at) : null,
        viewerId: user?.user_id,
        ownerId: expense.user_id,
      })
    : null;
  const sentBack =
    !!expense && expense.status === "draft" && !!expense.decided_at;
  // Phone layout: on a draft the next step is "attach receipt → submit", so
  // the actions sit AFTER the receipts card instead of above it.
  const actionsAfterReceipts = expense?.status === "draft";

  return (
    <ExpensesModuleShell
      // The record: "Expense — Meera Joshi, 3 Nov 2026" (no name on your
      // own), so the breadcrumb and the tab title name it.
      title={
        expense
          ? `Expense — ${
              isOwner ? "" : `${dir.label(expense.user_id, expense.user_name, false)}, `
            }${fmtDate(expense.expense_date)}`
          : "Expense"
      }
      actions={
        // Phones only: the desktop breadcrumb goes to the same place.
        <Button asChild variant="outline" className="gap-1.5 lg:hidden">
          <Link to={back.to} params={{ tenantSlug }} search={back.search}>
            <ArrowLeft className="h-4 w-4" />
            {back.label}
          </Link>
        </Button>
      }
    >
      <ExpenseFeatureGate>
        {isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertTitle>Could not load this expense</AlertTitle>
            <AlertDescription>
              {toApiError(error).message}
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && !expense && (
          <Alert>
            <AlertTitle>Expense not found</AlertTitle>
            <AlertDescription>
              This expense doesn’t exist or isn’t visible to your role.{" "}
              <Link
                to="/app/$tenantSlug/expenses" params={{ tenantSlug }}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Back to Expenses
              </Link>
              .
            </AlertDescription>
          </Alert>
        )}

        {/* An approver viewing someone's claim stays on Inbox (QA #15). */}
        {expense && <RequestSurfaceClaim ownerId={expense.user_id} />}
        {expense && (
          <div className="space-y-5">
            {decidedElsewhere && (
              <Alert>
                <AlertTitle>Already decided</AlertTitle>
                <AlertDescription>{decidedElsewhere}</AlertDescription>
              </Alert>
            )}
            {sentBack && isOwner && (
              <Alert>
                <AlertTitle>Sent back for changes</AlertTitle>
                <AlertDescription>
                  {dir.label(expense.approver_id, expense.approver_name)} asked
                  you to change this claim
                  {expense.decision_notes ? `: “${expense.decision_notes}”` : "."}{" "}
                  Make the change — use Edit to fix the amount, date or
                  details, or attach the receipt — then submit it again.
                </AlertDescription>
              </Alert>
            )}
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">
                      {categoryLabel} ·{" "}
                      {formatPaise(expense.amount_paise)}
                    </CardTitle>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {isOwner
                        ? "Your claim"
                        : dir.label(expense.user_id, expense.user_name)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isOwner && expense.status === "draft" && (
                      <EditDraftExpenseDialog expense={expense} />
                    )}
                    <StatusBadge status={expense.status} />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <dl className="grid gap-3 sm:grid-cols-2">
                  <Row
                    label="Amount"
                    value={formatPaise(expense.amount_paise)}
                  />
                  <Row
                    label="Expense Date"
                    value={
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                        {fmtDate(expense.expense_date)}
                      </span>
                    }
                  />
                  <Row label="Category" value={categoryLabel} />
                  <Row
                    label="Merchant"
                    value={
                      expense.merchant ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Store className="h-3.5 w-3.5 text-muted-foreground" />
                          {expense.merchant}
                        </span>
                      ) : (
                        "—"
                      )
                    }
                  />
                  {showFiledRow(expense.status) && (
                    <Row
                      label="Filed"
                      value={fmtDateTime(expense.created_at)}
                    />
                  )}
                  <Row
                    label="Currency"
                    value={expense.currency || "INR"}
                  />
                </dl>
                {expense.description && (
                  <div className="mt-4 rounded-md border border-input bg-muted/30 p-3">
                    <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <FileText className="h-3.5 w-3.5" />
                      Description
                    </p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
                      {expense.description}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Status &amp; Decision
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <dl className="grid gap-3 sm:grid-cols-2">
                  <Row
                    label="Current Status"
                    value={<StatusBadge status={expense.status} />}
                  />
                  {expense.decided_at ? (
                    <>
                      <Row
                        label={sentBack ? "Sent Back" : "Decided"}
                        value={fmtDateTime(expense.decided_at)}
                      />
                      <Row
                        label={sentBack ? "Sent Back By" : "Decided By"}
                        value={
                          expense.approver_id != null
                            ? decidedByLabel(
                                dir.label(
                                  expense.approver_id,
                                  expense.approver_name,
                                ),
                                expense.approver_id,
                                expense.user_id,
                              )
                            : "—"
                        }
                      />
                      <Row
                        label="Decision Note"
                        value={expense.decision_notes || "—"}
                      />
                    </>
                  ) : (
                    <div className="sm:col-span-2">
                      <dt className="sr-only">Decision</dt>
                      <dd className="rounded-md border border-dashed border-input px-3 py-2 text-sm text-muted-foreground">
                        {undecidedLine(
                          expense.status,
                          expense.awaiting_approver_names,
                        )}
                      </dd>
                    </div>
                  )}
                  {expense.status === "reimbursed" && (
                    <Row
                      label="Reimbursed Via"
                      value={
                        expense.reimbursed_batch_id != null
                          ? `Batch #${expense.reimbursed_batch_id}`
                          : "—"
                      }
                    />
                  )}
                </dl>

                {!actionsAfterReceipts && (
                  <div className="pt-1">
                    <ExpenseActions expense={expense} onDecided={onDecided} />
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Receipts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {showReceiptRequiredWarning({
                  requiresReceipt: !!category?.requires_receipt,
                  status: expense.status,
                  canAct: isOwner || isExpenseAdmin,
                  receiptCount: receiptsQ.data?.receipts.length,
                }) && (
                  <Alert>
                    <AlertTitle>A receipt is required</AlertTitle>
                    <AlertDescription>
                      {categoryLabel} needs at least one receipt.
                      Add one below, then submit the claim.
                    </AlertDescription>
                  </Alert>
                )}
                <ReceiptManager
                  expenseId={expense.id}
                  status={expense.status}
                  canModify={canModify}
                />
              </CardContent>
            </Card>

            {actionsAfterReceipts && (
              <Card>
                <CardContent className="pt-6">
                  <ExpenseActions expense={expense} onDecided={onDecided} />
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </ExpenseFeatureGate>
    </ExpensesModuleShell>
  );
}
